/**
 * HR 사이드바 메뉴 데이터
 *
 * 디자인 원본: hr-system/project/components/Layout.jsx (HR_NAV)
 *
 * 구조
 * - top items: "구성원"(본인 프로필), "대시보드"
 * - 그룹 섹션: 구성원 관리 · 라이프사이클 · 성장 & 평가 · 보상 & 복지 · 조직 인사이트
 *
 * 각 항목에 `key`를 부여(S2 현재는 권한 체크를 우회하지만 Phase 2에서 사용).
 */

export interface HrMenuItem {
  id: string;
  title: string;
  path: string;
  key: string;
  icon?: string;   // 텍스트 모노라인 아이콘 (디자인 파일 그대로)
  requiresAdmin?: boolean;  // Sprint 7B-12A: admin/superadmin 만 사이드바에 표시
}

export interface HrMenuGroup {
  type: 'group';
  id: string;
  title: string;
  icon: string;
  children: HrMenuItem[];
}

export interface HrMenuTopItem {
  type: 'item';
  id: string;
  title: string;
  icon: string;
  path: string;
  key: string;
  requiresAdmin?: boolean;
}

export type HrMenuEntry = HrMenuGroup | HrMenuTopItem;

export const hrMenuSections: HrMenuEntry[] = [
  {
    type: 'item',
    id: 'hr-me',
    title: '구성원',
    icon: '☺',
    path: '/hr/me',
    key: 'hr-me',
  },
  // Sprint 47-D — HR 설정 진입점은 사이드바 하단 "관리" 버튼으로 단일화. top item 제거.
  {
    type: 'item',
    id: 'hr-dashboard',
    title: '대시보드',
    icon: '▣',
    path: '/hr/dashboard',
    key: 'hr-dashboard',
  },
  // 내 할 일 (2026-09-11) — 개인 업무와 동료 간 업무 요청.
  //   회의에서 나온 업무(성장&평가 > 진행 프로젝트)와 **저장소가 다르다**(대표 결정).
  {
    type: 'item',
    id: 'hr-todo',
    title: '내 할 일',
    icon: '☑',
    path: '/hr/todo',
    key: 'hr-todo',
  },
  // Sprint 47-G — 10명 미만 회사 적합도 검토 결과: '나의 트렌드'·'공지사항' top item 은 메뉴에서 제거.
  // (라우트 자체는 유지 — 직접 URL 진입 가능)
  {
    type: 'group',
    id: 'hr-people',
    title: '구성원 관리',
    icon: '◉',
    children: [
      { id: 'hr-search', title: '구성원 검색', path: '/hr/search', key: 'hr-search' },
      { id: 'hr-org', title: '조직도', path: '/hr/org-chart', key: 'hr-org' },
      { id: 'hr-responsibility', title: '업무분장', path: '/hr/responsibility', key: 'hr-responsibility' },
    ],
  },
  {
    type: 'group',
    id: 'hr-attendance-leaves',
    title: '근태 & 휴가',
    icon: '⏱',
    children: [
      { id: 'hr-my-attendance', title: '내 근태', path: '/hr/my-attendance', key: 'hr-my-attendance' },
      { id: 'hr-my-status', title: '내 수당 및 연차 현황', path: '/hr/my-status', key: 'hr-my-status' },
      { id: 'hr-team-attendance', title: '팀 근태', path: '/hr/team-attendance', key: 'hr-team-attendance' },
      { id: 'hr-team-schedule', title: '팀 일정', path: '/hr/team-schedule', key: 'hr-team-schedule' },
      { id: 'hr-leaves', title: '휴가 관리', path: '/hr/leaves', key: 'hr-leaves' },
    ],
  },
  // Sprint 47-B 이후: 모든 admin 항목은 "HR 설정" 단일 진입점(/hr/admin/settings)으로 통합.
  // 일반 사용자 메뉴만 그룹에 유지.
  {
    type: 'group',
    id: 'hr-approvals-admin',
    title: '결재 & 정책',
    icon: '✓',
    children: [
      { id: 'hr-approvals', title: '전자결재', path: '/hr/approvals', key: 'hr-approvals' },
    ],
  },
  {
    type: 'group',
    id: 'hr-lifecycle-docs',
    title: '문서 & 라이프사이클',
    icon: '↗',
    children: [
      { id: 'hr-onboarding', title: '온·오프보딩', path: '/hr/onboarding', key: 'hr-onboarding' },
      { id: 'hr-contracts', title: '계약 관리', path: '/hr/contracts', key: 'hr-contracts' },
      { id: 'hr-documents', title: '문서·증명서', path: '/hr/documents', key: 'hr-documents' },
    ],
  },
  {
    type: 'group',
    id: 'hr-growth',
    title: '성장 & 평가',
    icon: '↑',
    children: [
      { id: 'hr-goals', title: '목표·OKR', path: '/hr/goals', key: 'hr-goals' },
      { id: 'hr-reviews', title: '성과 리뷰', path: '/hr/reviews', key: 'hr-reviews' },
      { id: 'hr-tasks', title: '진행 프로젝트', path: '/hr/tasks', key: 'hr-tasks' },
      { id: 'hr-education', title: '교육 관리', path: '/hr/education', key: 'hr-education' },
      { id: 'hr-meetings', title: '미팅', path: '/hr/meetings', key: 'hr-meetings' },
    ],
  },
  {
    type: 'group',
    id: 'hr-compensation',
    title: '보상 & 복지',
    icon: '₩',
    children: [
      { id: 'hr-payroll', title: '급여', path: '/hr/payroll', key: 'hr-payroll' },
      { id: 'hr-benefits', title: '복지', path: '/hr/benefits', key: 'hr-benefits' },
      { id: 'hr-insurance', title: '보험', path: '/hr/insurance', key: 'hr-insurance' },
    ],
  },
  {
    type: 'group',
    id: 'hr-insights',
    title: '조직 인사이트',
    icon: '▲',
    children: [
      { id: 'hr-trend', title: '조직 트렌드', path: '/hr/trend', key: 'hr-trend' },
      { id: 'hr-reports', title: '리포트', path: '/hr/reports', key: 'hr-reports' },
    ],
  },
];

/** 모든 HR 메뉴 key 목록 (권한 테이블 시드용) */
export const HR_MENU_KEYS: string[] = hrMenuSections.flatMap((s) =>
  s.type === 'item' ? [s.key] : s.children.map((c) => c.key),
);
